import ExoStepper, { FinalPage } from "../exo/ExoStepper";
import { useTheme } from "@mui/material";
import { tokens } from "../../global/theme/tokens";
import { API_PATCH, API_POST, DBRequest, useConfig } from "../../api/api";
import findDifferences from "../exo/findDifferencesInDataCollection";
import { useMemo } from "react";
import StepRecipient from "../steps/StepRecipient";
import StepContractTemplate from "../steps/StepContractTemplate";
import StepContractVars from "../steps/StepContractVars";
import { useTranslation } from "react-i18next";
import ExoDialog from "../exo/ExoDialog";
import StepDelivery from "../steps/StepDelivery";
import StepUser from "../steps/StepUser";
import { StepTemplate } from "../steps/StepTemplate";
import StepTemplateMetaFields from "../steps/StepTemplateMetaFields";
import { PositionImage } from "../exo/positions/collapse-modules/PositionImage";
import { PositionDescription } from "../exo/positions/collapse-modules/PositionDescription";
import { PositionNumber } from "../exo/positions/collapse-modules/PositionNumber";
import { PositionText } from "../exo/positions/collapse-modules/PositionText";
import { PositionCheckbox } from "../exo/positions/collapse-modules/PositionCheckbox";
import { PositionRebate } from "../exo/positions/collapse-modules/PositionRebate";
import { PositionCopy } from "../exo/positions/collapse-modules/PositionCopy";
import { PositionPreCalculation } from "../exo/positions/collapse-modules/PositionPreCalculation";
import StepPositions from "../steps/StepPositions";

const NewContract = ({
  contracts,
  setContracts,
  onClose,
  open,
  startData,
  onUpdate,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const isEdit = Boolean(startData && startData.id);

  // map an existing contract (GET contracts/{id}) to the stepper data structure
  const startDataCollection = useMemo(() => {
    if (!startData) return {};
    const { contractPreset, contractVariables, ...rest } = startData;
    return {
      ...rest,
      preset: startData.preset || contractPreset,
      contractVariables: contractVariables || [],
      positions: startData.positions || [],
    };
  }, [startData]);

  const steps = [
    {
      label: t("Contract Template"),
      content: ({
        dataCollection,
        updateValidation,
        updateDataCollection,
        activeStep,
        setActiveStep,
      }) => (
        <StepContractTemplate
          updateDataCollection={updateDataCollection}
          dataCollection={dataCollection}
          updateValidation={updateValidation}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
        />
      ),
    },
    {
      label: t("Contract Info"),
      content: ({
        dataCollection,
        updateValidation,
        updateDataCollection,
        activeStep,
        setActiveStep,
      }) => (
        <StepContractVars
          updateDataCollection={updateDataCollection}
          dataCollection={dataCollection}
          updateValidation={updateValidation}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
        />
      ),
    },
    {
      label: t("Recipient"),
      content: ({ dataCollection, updateValidation, updateDataCollection }) => (
        <StepRecipient
          updateDataCollection={updateDataCollection}
          dataCollection={dataCollection}
          updateValidation={updateValidation}
        />
      ),
    },
    {
      label: t("Template"),
      content: ({
        dataCollection,
        updateValidation,
        updateDataCollection,
        activeStep,
        setSteps,
      }) => (
        <StepTemplate
          updateDataCollection={updateDataCollection}
          dataCollection={dataCollection}
          updateValidation={updateValidation}
          templateType="contract"
          activeStep={activeStep}
          setSteps={setSteps}
        />
      ),
    },
    {
      label: t("Additional"),
      content: ({ dataCollection, updateValidation, updateDataCollection }) => (
        <StepTemplateMetaFields
          updateDataCollection={updateDataCollection}
          dataCollection={dataCollection}
          updateValidation={updateValidation}
          templateMetaData={dataCollection.templateMetaData}
        />
      ),
      isHiddenFunction: (data) =>
        !Boolean(data.templateMetaData?.metaFields?.length > 0),
    },
    {
      label: t("Invoice Template"),
      content: ({ dataCollection, updateValidation, updateDataCollection }) => (
        <StepTemplate
          updateDataCollection={updateDataCollection}
          dataCollection={dataCollection}
          updateValidation={updateValidation}
          templateType="invoice"
          templateKey="invoiceTemplateId"
        />
      ),
    },
    {
      label: t("Invoice Positions"),
      content: ({ dataCollection, updateValidation, updateDataCollection }) => (
        <StepPositions
          updateDataCollection={updateDataCollection}
          dataCollection={dataCollection}
          updateValidation={updateValidation}
          hideHOAI={true}
          collapseModules={[
            {
              component: PositionDescription,
              key: "description",
              label: "Description",
            },
            {
              component: PositionNumber,
              key: "taxRate",
              label: "Tax Rate",
              unit: "%",
              value: 19,
              decimalPlaces: 0,
            },
            {
              component: PositionText,
              key: "unit",
              label: "Unit",
            },
          ]}
          columns={[
            {
              text: t("Position"),
              width: "80px",
              align: "left",
              key: "position",
              className: "monospace justify-center",
              number: true,
              decimalPlaces: 2,
              editable: false,
              hidden: false,
              default: 1,
            },
            {
              text: t("Description"),
              width: "auto",
              align: "left",
              key: "name",
              editable: true,
              hidden: false,
              default: "",
            },
            {
              text: t("Amount per Month"),
              width: "180px",
              align: "center",
              key: "amount",
              className: "monospace",
              editable: true,
              hidden: false,
              default: 0,
            },
            {
              text: t("Price"),
              width: "120px",
              align: "right",
              key: "price",
              className: "monospace",
              unit: " €",
              editable: true,
              hidden: false,
              default: 0,
            },
            {
              key: "id",
              hidden: true,
              default: 0,
            },
            {
              key: "description",
              hidden: true,
              default: "",
            },
            {
              key: "positionableId",
              hidden: true,
              default: null,
            },
            {
              key: "positionableType",
              hidden: true,
              default: "custom",
            },
            {
              text: "",
              key: "delete",
              width: "37px",
            },
          ]}
          showVars
          hideSum
          hidePostPositions
          hideImport
        />
      ),
    },
    {
      label: t("Contact Person"),
      content: ({ dataCollection, updateValidation, updateDataCollection }) => (
        <StepUser
          updateDataCollection={updateDataCollection}
          dataCollection={dataCollection}
          updateValidation={updateValidation}
        />
      ),
    },
    {
      label: t("Delivery"),
      content: ({ dataCollection, updateValidation, updateDataCollection }) => (
        <StepDelivery
          updateDataCollection={updateDataCollection}
          dataCollection={dataCollection}
          updateValidation={updateValidation}
        />
      ),
    },
  ];
  const config = useConfig();
  const finalStep = {
    operation: ({
      dataCollection,
      handleResponse,
      setIsLoading,
      handleError,
    }) => {
      setIsLoading(true);

      const { contractVariables, ...data } = dataCollection;
      let diff = findDifferences(startDataCollection, data);
      // preset and vars are always required to regenerate the contract
      diff = { ...diff, preset: data.preset, vars: data.vars };

      DBRequest({
        config,
        path: isEdit ? `contracts/${startData.id}` : "contracts",
        method: isEdit ? API_PATCH(diff) : API_POST(data),
        onResponse: handleRes,
        onLoading: setIsLoading,
        onError: handleError,
      });

      function handleRes(res) {
        handleResponse(res);
        if (onUpdate) onUpdate(res);
      }
    }, // your operation with the data
    content: (response) => (
      <FinalPage response={response} data={contracts} setData={setContracts} />
    ), // component with final operations and the feedback
  };

  return (
    <ExoDialog open={open} onClose={() => onClose()}>
      <ExoStepper
        label={isEdit ? t("Edit Contract") : t("New Contract")}
        draftType={"contract"}
        onClose={onClose}
        data={steps}
        finalStep={finalStep}
        startDataCollection={startDataCollection}
      />
    </ExoDialog>
  );
};

export default NewContract;
